import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Bug, CheckCircle2, FileText, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/hooks/auth";
import { supabase } from "@/integrations/supabase/client";

type Prioridade = "Alta" | "Média" | "Baixa";
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const BugReportSchema = z.object({
  titulo: z
    .string()
    .trim()
    .min(3, "O título deve ter no mínimo 3 caracteres")
    .max(200, "O título deve ter no máximo 200 caracteres"),
  descricao: z
    .string()
    .trim()
    .min(10, "Descreva o problema com mais detalhes (mín. 10 caracteres)"),
  prioridade: z.enum(["Alta", "Média", "Baixa"], {
    required_error: "Selecione a prioridade",
  }),
});

type BugReportFormValues = z.infer<typeof BugReportSchema>;

interface BugReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CreatedTicket {
  id: string;
  numero_ticket: number;
  titulo: string;
  prioridade: string;
  created_at?: string;
}

interface AttachmentPayload {
  nome: string;
  tipo: string;
  conteudo_base64: string;
}

interface HelpdeskCreateTicketResponse {
  ticket: CreatedTicket;
  attachments_uploaded?: number;
  attachment_errors?: string[];
  error?: string;
  details?: string;
}

const PRIORITY_OPTIONS: {
  value: Prioridade;
  label: string;
  activeClass: string;
  inactiveClass: string;
}[] = [
  {
    value: "Alta",
    label: "Alta Prioridade",
    activeClass:
      "border-red-500 bg-red-500 text-white shadow-sm hover:bg-red-500",
    inactiveClass:
      "border-red-300 text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10",
  },
  {
    value: "Média",
    label: "Média Prioridade",
    activeClass:
      "border-amber-500 bg-amber-500 text-white shadow-sm hover:bg-amber-500",
    inactiveClass:
      "border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-500/10",
  },
  {
    value: "Baixa",
    label: "Baixa Prioridade",
    activeClass:
      "border-emerald-500 bg-emerald-500 text-white shadow-sm hover:bg-emerald-500",
    inactiveClass:
      "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/40 dark:text-emerald-400 dark:hover:bg-emerald-500/10",
  },
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Erro ao ler arquivo"));
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const BugReportModal = ({ open, onOpenChange }: BugReportModalProps) => {
  const { user } = useCurrentUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTicket, setCreatedTicket] = useState<CreatedTicket | null>(null);

  const form = useForm<BugReportFormValues>({
    resolver: zodResolver(BugReportSchema),
    defaultValues: {
      titulo: "",
      descricao: "",
      prioridade: "Média",
    },
  });

  useEffect(() => {
    if (!open) {
      // Reset state quando o modal fecha
      const t = setTimeout(() => {
        form.reset({ titulo: "", descricao: "", prioridade: "Média" });
        setFiles([]);
        setCreatedTicket(null);
        setIsSubmitting(false);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open, form]);

  const handleFilesSelected = (selected: FileList | null) => {
    if (!selected || selected.length === 0) return;
    const incoming = Array.from(selected);
    const tooLarge = incoming.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (tooLarge) {
      toast.error(
        `O arquivo "${tooLarge.name}" excede o limite de ${MAX_FILE_SIZE_MB}MB.`,
      );
    }
    const valid = incoming.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    setFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFileAt = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (values: BugReportFormValues) => {
    const email = user?.email?.trim();
    if (!email) {
      toast.error("Não foi possível identificar o seu e-mail. Faça login novamente.");
      return;
    }

    setIsSubmitting(true);
    try {
      const attachments: AttachmentPayload[] = await Promise.all(
        files.map(async (file) => ({
          nome: file.name,
          tipo: file.type || "application/octet-stream",
          conteudo_base64: await fileToBase64(file),
        })),
      );

      const { data, error } = await supabase.functions.invoke<HelpdeskCreateTicketResponse>(
        "helpdesk-create-ticket",
        {
          body: {
            titulo: values.titulo.trim(),
            descricao: values.descricao.trim(),
            prioridade: values.prioridade,
            solicitante_email: email,
            attachments,
          },
        },
      );

      if (error) {
        throw new Error(error.message || "Falha ao chamar a edge function de ticket");
      }

      if (!data || data.error || !data.ticket) {
        throw new Error(data?.details || data?.error || "Resposta inválida da edge function");
      }

      if (Array.isArray(data.attachment_errors) && data.attachment_errors.length > 0) {
        toast.warning(
          `Ticket #${data.ticket.numero_ticket} criado, mas alguns anexos falharam.`,
          { description: data.attachment_errors.join(" • ") },
        );
      }

      setCreatedTicket(data.ticket);
    } catch (err) {
      toast.error("Não foi possível enviar o relatório de bug.", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isSubmitting) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        {createdTicket ? (
          <div className="flex flex-col items-center text-center px-8 py-10">
            <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <DialogTitle className="text-xl font-semibold mb-1">
              Bug reportado com sucesso!
            </DialogTitle>
            <DialogDescription className="mb-5">
              Recebemos seu relatório e nossa equipe já foi notificada.
            </DialogDescription>
            <div className="w-full rounded-lg border bg-muted/40 px-4 py-3 mb-6 text-left">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Número do ticket
              </div>
              <div className="text-2xl font-semibold mt-0.5">
                #{createdTicket.numero_ticket}
              </div>
              <div className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {createdTicket.titulo}
              </div>
            </div>
            <Button onClick={handleClose} className="w-full sm:w-auto">
              Fechar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center">
                  <Bug className="h-5 w-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg">Relatar Bug</DialogTitle>
                  <DialogDescription>
                    Conte o que aconteceu para que possamos investigar.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="px-6 py-5 space-y-5"
              >
                <FormField
                  control={form.control}
                  name="titulo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Título <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex.: Erro ao criar lead na tela de cadastro"
                          autoFocus
                          maxLength={200}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Descrição <span className="text-destructive">*</span>
                      </FormLabel>
                      <p className="text-xs text-muted-foreground -mt-1 mb-2">
                        Descreva o bug com detalhes: passos para reproduzir,
                        comportamento esperado e o que aconteceu.
                      </p>
                      <FormControl>
                        <Textarea
                          rows={6}
                          placeholder="Inclua qualquer informação relevante para reproduzir o problema..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prioridade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Prioridade <span className="text-destructive">*</span>
                      </FormLabel>
                      <p className="text-xs text-muted-foreground -mt-1 mb-2">
                        O quanto este problema está impactando seu trabalho?
                      </p>
                      <FormControl>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {PRIORITY_OPTIONS.map((opt) => {
                            const active = field.value === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => field.onChange(opt.value)}
                                aria-pressed={active}
                                className={cn(
                                  "h-10 rounded-md border-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                  active ? opt.activeClass : opt.inactiveClass,
                                )}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <FormLabel className="block">Anexos</FormLabel>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Capturas de tela, vídeos curtos ou logs ajudam a investigar
                    mais rápido. Máx {MAX_FILE_SIZE_MB}MB por arquivo.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={(e) => handleFilesSelected(e.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 rounded-md border border-dashed border-input bg-background hover:bg-muted/40 transition-colors px-4 py-6 text-sm text-muted-foreground"
                  >
                    <Paperclip className="h-4 w-4" />
                    Clique para selecionar arquivos
                  </button>

                  {files.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {files.map((file, idx) => (
                        <li
                          key={`${file.name}-${idx}`}
                          className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2"
                        >
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm truncate">{file.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatBytes(file.size)}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeFileAt(idx)}
                            aria-label={`Remover ${file.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t -mx-6 px-6 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={isSubmitting}
                    className="gap-2"
                  >
                    <Bug className="h-4 w-4" />
                    {isSubmitting ? "Enviando..." : "Enviar relatório"}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

